/*
 * Copyright (c) 2021, WSO2 Inc. (http://www.wso2.com). All Rights Reserved.
 *
 * This software is the property of WSO2 Inc. and its suppliers, if any.
 * Dissemination of any information or reproduction of any material contained
 * herein is strictly forbidden, unless permitted by WSO2 in accordance with
 * the WSO2 Commercial License available at http://wso2.com/licenses.
 * For specific language governing the permissions and limitations under
 * this license, please see the license as well as any agreement you’ve
 * entered into with WSO2 governing the purchase of this software and any
 * associated services.
 */

import React, { ChangeEvent, forwardRef, Ref, RefAttributes, useCallback, useEffect, useState } from "react";
import { connect } from "react-redux";
import { FormattedMessage, useIntl } from 'react-intl';
import debounce from "lodash.debounce";

import { Box, Dialog, DialogContent, DialogTitle, IconButton, TextField, Typography } from "@material-ui/core";
import { ArrowDropDown, ChevronLeft, ChevronRight, Close, Info, Search } from "@material-ui/icons";
import MaterialTable, { Column, MTablePagination } from "@material-table/core";

import {
    ApiReducer, ApplicationReducer, ApplicationState, SubscriptionsReducer, ToSubscribeAttributes, UserReducer
} from "../../../data/interfaces";
import { fetchApiListAuthenticated } from "../../../data/operations";
import { PreLoaderLogo } from "../../../elements/PreLoaderLogo";

import Actions from "./Actions";
import { useStyles } from './styles';
import { date } from "joi";
import { API_COUNT_PER_PAGE, API_PAGINATION_QUERY, MAJOR_VERSION, VALUE_ZERO } from "../../../data/constants";
import { formatSubscriptionApiVersion, getApiMajorVersionRange, Version } from '.';
import Settings from '../../../conf/settings';

interface CreateSubscriptionProps {
    open: boolean,
    onClose: () => void,
    applicationReducer: ApplicationReducer,
    subscriptionReducer: SubscriptionsReducer,
    apiReducer: ApiReducer,
    userReducer: UserReducer,
    dispatchFetchApis: (
        organizationId: string,
        pagination?: string,
        requireThumbnails?: boolean,
        aggregateBy?: string
    ) => void,
}

function CreateSubscription(props: CreateSubscriptionProps) {
    const { open, onClose, subscriptionReducer, applicationReducer, apiReducer, userReducer, dispatchFetchApis } = props;
    const classes = useStyles();
    const intl = useIntl();
    const subscriptions = subscriptionReducer?.subscriptions;
    const apis = apiReducer?.apiList;
    const isApisFetching = apiReducer?.loading;
    const { orgs: userOrganization, selectedOrg } = userReducer;
    const isSubscriptionsFetching = subscriptionReducer?.isLoading;
    const appList = applicationReducer?.applications?.list;
    const application = appList?.length > 0 ? appList[0] : undefined;
    const applicationId = application?.applicationId;
    const [searchQuery, setSearchQuery] = useState('');
    const [updatedApiList, setUpdatedApiList] = useState<ToSubscribeAttributes[]>([]);
    const [subscribedApiList, setSubscribedApiList] = useState<{name: string, versionRange: string}[]>([]);
    const [currentPage, setCurrentPage] = useState(0);

    const searchFieldPlaceholder = intl.formatMessage({
        id: 'application.subscriptions.createDialog.searchInput.placeHolder',
        defaultMessage: 'Search API'
    });

    const nameColumn = intl.formatMessage({
        id: 'application.subscriptions.createDialog.subscriptionTable.column.name',
        defaultMessage: 'Name'
    });

    const versionColumn = intl.formatMessage({
        id: 'application.subscriptions.createDialog.subscriptionTable.column.version',
        defaultMessage: 'Version'
    });

    const policyColumn = intl.formatMessage({
        id: 'application.subscriptions.createDialog.subscriptionTable.column.policy',
        defaultMessage: 'Policy'
    });

    const tableSearchPlaceholder = intl.formatMessage({
        id: 'application.subscriptions.createDialog.subscriptionTable.searchInput.placeHolder',
        defaultMessage: 'Search API'
    });

    const handleSearchFieldChange = (event: ChangeEvent<HTMLInputElement>) => {
        handleApiSearch(event.target.value);
        setSearchQuery(event.target.value);
    };

    const handleApiSearch = useCallback(debounce((apiDisplayName: string) => {
        let paginationQuery = API_PAGINATION_QUERY
            .replace('{LIMIT}', API_COUNT_PER_PAGE.toString())
            .replace('{OFFSET}', VALUE_ZERO.toString());

        if (apiDisplayName) {
            paginationQuery = paginationQuery.concat("display-name:" + encodeURI(apiDisplayName));
        }
        setCurrentPage(0);
        if (Settings.intelligentRoutingFeatureEnabled) {
            dispatchFetchApis(selectedOrg.uuid, paginationQuery, false, MAJOR_VERSION);
        } else {
            dispatchFetchApis(selectedOrg.uuid, paginationQuery, false);
        }
    }, 700), []);

    const RenderActionsColumn = (rowData: ToSubscribeAttributes) => {
        if (rowData?.availablePolicies) {
            return (
                <Actions
                    applicationId={applicationId}
                    apiId={rowData?.apiId}
                    apiName={rowData?.api}
                    apiVersion={rowData?.version}
                    throttlingPolicies={rowData?.availablePolicies}
                    alreadySubscribed={rowData?.hasSubscribed}
                />);
        } else {
            return (
                <FormattedMessage
                    id='application.subscriptions.createDialog.actionLabel'
                    defaultMessage='Subscribed'
                />
            );
        }
    };

    const RenderVersionColumn = (rowData: ToSubscribeAttributes) => {
        return (
            <Version version={rowData.version || ''} />
        );
    }

    const getSearchIcon = (props: RefAttributes<SVGSVGElement>,
        ref: Ref<SVGSVGElement>) => <Search {...props} ref={ref} />;

    const getNextPageIcon = (props: RefAttributes<SVGSVGElement>,
        ref: Ref<SVGSVGElement>) => <ChevronRight {...props} ref={ref} />;

    const getPreviousPageIcon = (props: RefAttributes<SVGSVGElement>,
        ref: Ref<SVGSVGElement>) => <ChevronLeft {...props} ref={ref} />;

    const getArrowIcon = (props: RefAttributes<SVGSVGElement>,
        ref: Ref<SVGSVGElement>) => <ArrowDropDown{...props} ref={ref} />;

    const SearchField = () => {
        return (
            <div className={classes.searchField}>
                <TextField
                    onChange={handleSearchFieldChange}
                    value={searchQuery}
                    placeholder={searchFieldPlaceholder}
                />
            </div>
        );
    };

    const toSubColumns = [
        { title: nameColumn, field: 'api', width: '40%' },
        { title: versionColumn, field: 'version', width: '20%', render: RenderVersionColumn },
        { title: policyColumn, field: 'policy', width: '40%', render: RenderActionsColumn }
    ] as Column<ToSubscribeAttributes>[];

    const tableIcons = {
        Search: forwardRef(getSearchIcon),
        NextPage: forwardRef(getNextPageIcon),
        PreviousPage: forwardRef(getPreviousPageIcon),
        SortArrow: forwardRef(getArrowIcon)
    };

    const handlePageChange = (event: React.MouseEvent<HTMLButtonElement> | null, page: number) => {
        setCurrentPage(page);
        let paginationQuery = API_PAGINATION_QUERY
            .replace('{LIMIT}', API_COUNT_PER_PAGE.toString())
            .replace('{OFFSET}', (page * API_COUNT_PER_PAGE).toString());

        if (searchQuery) {
            paginationQuery = paginationQuery.concat("display-name:" + encodeURI(searchQuery));
        }

        if (Settings.intelligentRoutingFeatureEnabled) {
            dispatchFetchApis(selectedOrg.uuid, paginationQuery, false, MAJOR_VERSION);
        } else {
            dispatchFetchApis(selectedOrg.uuid, paginationQuery, false);
        }
    }

    useEffect(() => {
        updateApiTableData();
    }, [apis]);

    useEffect(() => {
        updateSubscribedApiList();
    }, [subscriptions]);

    useEffect(() => {
        updateApiTableData();
    }, [subscribedApiList]);

    const pagination = (paginationProps: any) => {
        return (
            <MTablePagination
                {...paginationProps}
                onPageChange={handlePageChange}
                page={currentPage}
                rowsPerPage={API_COUNT_PER_PAGE}
                count={apis?.pagination.total}
                showFirstLastPageButtons={false}
                classes={{root: paginationProps.classes.root}}
            />
        );
    }

    const setTableData = () => {
        updateSubscribedApiList();
        const paginationQuery = API_PAGINATION_QUERY
            .replace('{LIMIT}', API_COUNT_PER_PAGE.toString())
            .replace('{OFFSET}', VALUE_ZERO.toString());
        if (Settings.intelligentRoutingFeatureEnabled) {
            dispatchFetchApis(selectedOrg.uuid, paginationQuery, false, MAJOR_VERSION);
        } else {
            dispatchFetchApis(selectedOrg.uuid, paginationQuery, false);
        }
    }

    const updateSubscribedApiList = () => {
        const subscribedApiList = subscriptions?.list?.map((subscription) => {
            const apiVersion = subscription?.apiInfo?.version;
            if (apiVersion) {
                const versionRange = Settings.intelligentRoutingFeatureEnabled && apiVersion.startsWith("v")
                    ? apiVersion.split('.')[0] : apiVersion;
                return { name: subscription.apiInfo?.name || "", versionRange };
            }
            return { name: subscription.apiInfo?.name || "", versionRange: "" };
        });
        setSubscribedApiList(subscribedApiList);
    }

    const updateApiTableData = () => {
        const data: ToSubscribeAttributes[] = [];
        if (apis?.count > 0) {
            apis?.list?.forEach((api) => {
                let subscribed = false;
                if (subscribedApiList?.find(subApi =>
                    subApi.name === api.name && (subApi.versionRange === getApiMajorVersionRange(api.version)
                    || subApi.versionRange === api.version)
                )) {
                    subscribed = true;
                }
                data.push({
                    api: api.displayName,
                    apiId: api.id,
                    version: formatSubscriptionApiVersion(api.version),
                    availablePolicies: api.throttlingPolicies,
                    hasSubscribed: subscribed
                })
            });
        }
        setUpdatedApiList(data);
    }

    const ToSubscribeTable = () => {
        if (updatedApiList.length > 0) {
            return (
                <div>
                    <div className={["config-list", classes.containerList].join(" ")}>
                        <MaterialTable
                            isLoading={false}
                            columns={toSubColumns}
                            options={{
                                sorting: false,
                                showTitle: false,
                                pageSize: 5,
                                actionsColumnIndex: -1,
                                showFirstLastPageButtons: false,
                                tableLayout: 'fixed',
                                search: false,
                                headerStyle: { height: 30 },
                                rowStyle: { height: 45 }
                            }}
                            icons={tableIcons}
                            data={updatedApiList}
                            localization={{ 
                                toolbar: { searchPlaceholder: tableSearchPlaceholder },
                                pagination: { nextTooltip: "Next", previousTooltip: "Previous" }
                            }}
                            components={{
                                Pagination: pagination
                            }}
                        />
                    </div>
                </div>
            );
        } else {
            return (
                <div className={classes.bottomSpace}>
                    <div className={classes.noDataInfo}>
                        <Box display="flex" flexDirection="row" justifyContent="space-between">
                            <Box>
                                <Typography variant='subtitle2' className={classes.noDataText}>
                                    <FormattedMessage
                                        id='application.subscriptions.apis.table.empty.label'
                                        defaultMessage='No APIs available to subscribe'
                                    />
                                </Typography>
                            </Box>
                            <Box display="flex">
                                <Info className={classes.infoIcon} />
                            </Box>
                        </Box>
                    </div>
                </div>
            );
        }
    };

    const ConditionalDialogContent = () => {
        if (isApisFetching || isSubscriptionsFetching) {
            return (
                <div className={classes.loaderContainer}>
                    <PreLoaderLogo size="medium" />
                </div>
            );
        } else {
            return (
                <ToSubscribeTable />
            );
        }
    }

    const DialogContents = () => {
        return (
            <DialogContent className={classes.dialogContent}>
                <Typography className={classes.primaryTitle}>
                    <FormattedMessage
                        id='application.subscriptions.createDialog.title'
                        defaultMessage='Add APIs'
                    />
                </Typography>
                {SearchField()}
                {ConditionalDialogContent()}
            </DialogContent>
        );
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            onEnter={setTableData}
            fullWidth={true}
            maxWidth="sm"
            classes={{ paper: classes.dialogRootStyle }}
        >
            <DialogTitle>
                {
                    onClose ? (
                        <IconButton
                            aria-label="close"
                            className={classes.closeButton}
                            onClick={onClose}
                            data-testid='subscription-dialog-close-btn'
                        >
                            <Close />
                        </IconButton>
                    ) : null
                }
            </DialogTitle>
            {DialogContents()}
        </Dialog>
    );
}

const mapDispatchToProps = {
    dispatchFetchApis: fetchApiListAuthenticated
};

const mapStateToProps = (state: ApplicationState) => {
    return {
        applicationReducer: state.applicationReducer,
        subscriptionReducer: state.subscriptionsReducer,
        apiReducer: state.apiReducer,
        userReducer: state.userReducer
    }
};

export default connect(mapStateToProps, mapDispatchToProps)(CreateSubscription);
